import * as React from 'react';
import { cn } from '../../lib/cn';

/**
 * PageShell — the canonical module-page layout per `01-build-brief.md` §5.
 *
 *   ┌────────────────────────────────────────────────────────────────┐
 *   │ [Page title]      [TimePeriodSelector + Refresh]               │
 *   ├────────────────────────────────────────────────────────────────┤
 *   │ KPI 1  KPI 2  KPI 3  KPI 4  KPI 5  (KPI 6)                     │
 *   ├────────────────────────────────────────────────────────────────┤
 *   │  ▓▓▓▓ BIG TREND CHART ▓▓▓▓                                     │
 *   ├────────────────────────────────────────────────────────────────┤
 *   │ Secondary widget A           Secondary widget B                │
 *   ├────────────────────────────────────────────────────────────────┤
 *   │ Bottom list (optional)                                         │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * Slot-based composition: pass children into the `kpis`, `chart`,
 * `secondary`, and `bottom` slots. The header/title row is built-in.
 *
 * Empty / red-connector variant: pass `connectorRed` and the kpis/chart/
 * secondary slots are replaced by a single "Connector not connected"
 * empty state that links to /admin/integrations.
 */

export interface PageShellProps {
  title: string;
  subtitle?: React.ReactNode;
  /** Top-right control row — typically <TimePeriodSelector /> + Refresh. */
  toolbar?: React.ReactNode;
  /** KPI strip — usually 4–6 <KpiCard>s. */
  kpis?: React.ReactNode;
  /** The big chart slot. */
  chart?: React.ReactNode;
  /** 2-up secondary widgets slot. */
  secondary?: React.ReactNode;
  /** Bottom list / table slot. */
  bottom?: React.ReactNode;
  /**
   * When the connector is in "red" status, override the body with a
   * single empty state. The toolbar still renders so the user can see
   * the period they were looking at.
   */
  connectorRed?: boolean;
  redEmptyState?: React.ReactNode;
  className?: string;
}

export function PageShell({
  title,
  subtitle,
  toolbar,
  kpis,
  chart,
  secondary,
  bottom,
  connectorRed,
  redEmptyState,
  className,
}: PageShellProps) {
  return (
    <div className={cn('flex flex-col gap-6 p-6', className)}>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-h1 font-semibold text-text-primary tracking-tight">{title}</h1>
          {subtitle ? <p className="text-body text-text-secondary">{subtitle}</p> : null}
        </div>
        {toolbar}
      </header>

      {connectorRed ? (
        redEmptyState ?? <DefaultRedEmpty />
      ) : (
        <>
          {kpis ? (
            <section
              className="grid gap-4"
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}
            >
              {kpis}
            </section>
          ) : null}
          {chart ? <section>{chart}</section> : null}
          {secondary ? (
            <section className="grid gap-4 md:grid-cols-2">{secondary}</section>
          ) : null}
          {bottom ? <section>{bottom}</section> : null}
        </>
      )}
    </div>
  );
}

function DefaultRedEmpty() {
  return (
    <div className="flex min-h-[420px] flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-border bg-bg-surface p-12 text-center">
      <div className="rounded-full bg-bg-elevated p-3">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-muted" aria-hidden>
          <path d="M12 22s-8-4-8-12V5l8-3 8 3v5c0 8-8 12-8 12z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </div>
      <div className="flex flex-col gap-1">
        <h2 className="text-h2 text-text-primary">Connector not connected</h2>
        <p className="text-body text-text-secondary max-w-md">
          The eZhire Operational DB connector is currently in <span className="text-accent-700 font-medium">red</span> status.
          Connect or test the connector to flow live data through these pages.
        </p>
      </div>
      <a
        href="/admin/integrations"
        className="inline-flex h-9 items-center rounded-md bg-accent-500 px-4 text-caption font-medium text-text-on-brand shadow-sm transition-colors hover:bg-accent-600"
      >
        Open Connectors
      </a>
    </div>
  );
}
